<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center px-4">
      <h5 class="pl-2 mb-0"> {{anomaly.id ? 'Modification de l\'anomalie '+anomaly.num : 'Création d\'une nouvelle anomalie'}}</h5>
      <div class="header-toolbar pr-2">
        <n-button type="primary" class="btn btn-sm btn-primary" @click.native="back()">
          <span>Retour</span>
        </n-button>
      </div>
    </div>
    <div class="card-body px-4" style="min-height: 450px">
      <div v-if="!isLoaded">
        <spinner></spinner>
      </div>
      <div v-if="isLoaded" class="row justify-content-center px-1 mt-4">
        <div class="col-md-4 col-xl-2">
          <label class="input-label">N° d'anomalie</label>
            <fg-input name="nume" placeholder="ex: ANO-22-06" v-validate="'required'"
                      :error="errors.first('nume')" v-model="anomaly.num" @focus="duplicateNum = false"
                      @blur="checkDuplicateNum()" :class="duplicateNum ? 'has-danger' : ''" />
            <slot name="helpBlock">
              <div class="text-danger invalid-feedback" style="display: block" :class="'mt-2'"
                   v-if="duplicateNum">
                Ce numéro est déjà utilisé
              </div>
            </slot>
        </div>
        <div class="col-md-4 col-xl-2">
          <label class="input-label">Date de réception</label>
          <fg-input type="date" name="received_at" v-validate="'required'"
                    :error="errors.first('received_at')" addon-left-icon="now-ui-icons far fa-calendar-alt"
                    v-model="anomaly.received_at">
            <flat-picker ref="received_at" slot-scope="{focus, blur}" :config="flatpickr_config"
                         class="datepicker" :placeholder="'Choisir une date'" v-model="anomaly.received_at">
            </flat-picker>
          </fg-input>
        </div>
        <div class="col-md-4 col-xl-2">
          <label class="input-label">Déclarée par</label>
          <fg-input class="select" v-model="anomaly.declared_by">
            <el-select id="controller" placeholder="Choisir un déclarant" v-model="anomaly.declared_by"
                       filterable>
              <el-option v-for="controller in controller.options" :key="controller.id"
                         :label="controller.name" :value="controller.id"></el-option>
            </el-select>
          </fg-input>
        </div>
      </div>
      <div v-if="isLoaded" class="row justify-content-center px-1 mt-2">
        <div class="col-md-4 col-xl-2">
          <label class="input-label">N° d'affaire</label>
          <fg-input class="select" v-model="anomaly.command_number" name="command_number">
            <el-select id="controller" placeholder="Choisir une commande" v-model="anomaly.command_number"
                       no-data-text="Aucune commande" no-match-text="Aucun résultat" loading-text="Chargement"
                       allow-create filterable clearable remote :remote-method="getRemoteCommand" :loading="!command.loaded && command.options.length !== 0">
              <el-option v-for="command in command.options" :key="command.id" :label="command.command_number" :value="command.command_number"/>
            </el-select>
          </fg-input>
        </div>
        <div class="col-md-4 col-xl-2">
          <label class="input-label">Article</label>
          <fg-input class="select" v-model="anomaly.article_name"  name="article_name" v-validate="'required'" :error="errors.first('article_name')">
            <el-select id="controller" placeholder="Choisir un article" v-model="anomaly.article_name"
                       no-data-text="Aucun article" no-match-text="Aucun résultat" loading-text="Chargement"
                       allow-create filterable clearable remote :remote-method="getRemoteArticle" :loading="!article.loaded && article.options.length !== 0">
              <el-option v-for="article in article.options" :key="article.id" :label="article.name" :value="article.name"/>
            </el-select>
          </fg-input>
        </div>
        <div class="col-md-2 col-xl-1">
          <label class="input-label">Quantité</label>
          <fg-input type='number' name="quantity" class="select"
                    v-validate="'min_value:1|required'" :error="errors.first('quantity')"
                    v-model="anomaly.quantity" />
        </div>
        <div class="col-md-4 col-xl-2">
          <label class="input-label">Client</label>
          <fg-input class="select" v-model="anomaly.customer_id" name="customer_id" v-validate="'required'" :error="errors.first('customer_id')">
            <el-select id="controller" placeholder="Choisir un client" v-model="anomaly.customer_id"
                       no-data-text="Aucun client" no-match-text="Aucun résultat" loading-text="Chargement"
                       allow-create filterable clearable remote :remote-method="getRemoteCustomer" :loading="!customer.loaded && customer.options.length !== 0">
              <el-option v-for="customer in customer.options" :key="customer.id" :label="customer.name" :value="customer.id"/>
            </el-select>
          </fg-input>
        </div>
      </div>
      <div v-if="isLoaded" class="row">
        <div class="col-xl-6">
          <h4 class="part-header">Imputation & Cause de l'anomalie:</h4>
          <div class="row">
            <div class="col-xl-12">
              <label class="input-label">Description</label>
              <fg-input v-model="anomaly.intern_observation" class="mb-3" name="intern_observation" v-validate="'required'" :error="errors.first('intern_observation')">
                <textarea class="form-control test" id="intern_observation"
                          v-model="anomaly.intern_observation" rows="4" maxlength="255"
                          placeholder="Veuillez saisir la description de l'anomalie.">
                </textarea>
                <p class="comment">{{ intern_observation_sizeLimit }}</p>
              </fg-input>
            </div>
            <div class="col-xl-6">
              <label class="input-label">Service</label>
              <fg-input class="select mb-3" v-model="anomaly.department">
                <el-select id="controller" placeholder="Choisir un service" v-model="anomaly.department"
                           filterable>
                  <el-option v-for="department in department.options" :key="department.id"
                             :label="department.name" :value="department.name"></el-option>
                </el-select>
              </fg-input>
            </div>
            <div class="col-xl-6">
              <label class="input-label">Cause</label>
              <fg-input class="mb-3" name="cause" placeholder="Mauvais cablage" v-model="anomaly.cause"/>
            </div>
          </div>
        </div>
        <div class="col-xl-6">
          <h4 class="part-header">Cloture de l'anomalie:</h4>
          <div class="row">
            <div class="col-xl-12">
              <label class="input-label">Action curative</label>
              <fg-input class="mb-3" name="comments" placeholder="Décrire l'action curative" v-model="anomaly.comments"/>
            </div>
            <div class="col-xl-4">
              <label class="input-label">Coût</label>
              <fg-input type='number' name="cost" placeholder="2000 €" class="select mb-3" v-model="anomaly.cost" />
            </div>
            <div class="col-xl-8">
              <label class="input-label">Détail du coût</label>
              <fg-input class="mb-3" name="cost_detail" placeholder="Description du coût" v-model="anomaly.cost_detail"/>
            </div>
            <div class="col-xl-4">
              <label class="input-label">Visa Responsable</label>
              <fg-input class="select mb-3" v-model="anomaly.archived_by">
                <el-select id="controller" placeholder="Choisir un responsable"
                           v-model="anomaly.archived_by" filterable>
                  <el-option v-for="controller in controller.options" :key="controller.id"
                             :label="controller.name" :value="controller.id"></el-option>
                </el-select>
              </fg-input>
            </div>
            <div class="col-xl-4">
              <label class="input-label">Visa Direction</label>
              <fg-input class="select mb-3" v-model="anomaly.directed_by">
                <el-select id="controller" placeholder="Approuvé par" v-model="anomaly.directed_by" filterable>
                  <el-option v-for="controller in controller.options" v-if="managers.includes(controller.id)" :key="controller.id"
                             :label="controller.name" :value="controller.id"/>
                </el-select>
              </fg-input>
            </div>
            <div class="col-xl-4">
              <label class="input-label">Date de cloture</label>
              <fg-input type="date" name="archived_at" addon-left-icon="now-ui-icons far fa-calendar-alt"
                        v-model="anomaly.archived_at">
                <flat-picker ref="received_at" slot-scope="{focus, blur}" :config="flatpickr_config"
                             class="datepicker" :placeholder="'Choisir une date'" v-model="anomaly.archived_at">
                </flat-picker>
              </fg-input>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoaded" class="d-flex justify-content-center">
        <button type="submit" class="col-2 btn btn-sm btn-primary radius" @click=" busy = true, save()"
                :disabled="busy">Enregistrer</button>
      </div>
    </div>
  </div>
</template>

<script>
import {Option, Select} from "element-ui";
import Spinner from "@/components/Spinner/Circle.vue";
import flatPicker from "vue-flatpickr-component";
import basic from "@/mixins/basic";
import Form from "vform";
import {French} from "flatpickr/dist/l10n/fr";

export default {
  name: "AnomalyForm",
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    // eslint-disable-next-line vue/no-unused-components
    Spinner,
    // eslint-disable-next-line vue/no-unused-components
    flatPicker
  },
  mixins: [basic],
  data() {
    return {
      isLoaded: false,
      busy : false,
      duplicateNum: false,
      flatpickr_config: {
        allowInput: true,
        wrap: true, // set wrap to true only when using 'input-group'
        altFormat: 'd/m/Y',
        altInput: true,
        mode: 'single',
        dateFormat: 'Y-m-d',
        locale: French,
      },

      controller: {
        options: [],
        loaded: false,
      },

      command: {
        options: [],
        loaded: false,
      },

      article: {
        options: [],
        loaded: false,
      },

      customer: {
        options: [],
        loaded: false,
      },

      department: {
        options: [],
        loaded: false,
      },

      managers: [],

      anomaly: new Form({
        id: null,
        num: null,
        received_at: null,
        declared_by: null,
        quantity: 1,
        cause: null,
        cost: null,
        customer_id: null,
        intern_observation: '',
        command_number: null,
        article_name: null,
        department: null,
        comments: null,
        archived_by: null,
        directed_by: null,
        cost_detail: null,
        archived_at: null
      })
    }
  },
  computed: {
    intern_observation_sizeLimit: function () {
      return this.anomaly.intern_observation === '' ? 'Taille max' + ': ' + 255 + ' caractères' : 'Restant' + ': ' + (255 - this.anomaly.intern_observation.length) + ' caractères'
    },
  },
  mounted() {
    // Remplir "controller_list" avec un appel GET Axios
    this.axios.get(process.env.VUE_APP_API_BASE_URL + `fnc/controller/list`).then((response) => {
      this.controller.options = response.data
      this.controller.options.sort((a, b) => a.name.localeCompare(b.name));
      this.controller.loaded = true
      if (window.App !== undefined) {
        this.anomaly.declared_by = window.App.id
      }
    })

    this.axios.get(process.env.VUE_APP_API_BASE_URL + `fnc/departments/list`).then((response) => {
      this.department.options = response.data
      this.department.loaded = true
      response.data.forEach(department => {
        this.managers.push(department.manager.id)
      });
    })

    if (this.$route.params.id !== undefined){
      // Dans ce cas, nous sommes sur un formulaire d'édition
      this.axios.get(process.env.VUE_APP_API_BASE_URL + `anomaly/`+this.$route.params.id).then((response) => {

        const data = response.data.anomaly;

        this.customer.options.push(data.customer)

        // Remplir uniquement les champs nécessaires dans le formulaire
        this.anomaly.fill({
          id: data.id,
          num: data.num,
          received_at: data.received_at,
          declared_by: data.declared_by ? data.declared_by.id : null,
          quantity: data.quantity,
          cause: data.cause,
          cost: data.cost,
          customer_id: data.customer ? data.customer.id : null,
          intern_observation: data.intern_observation,
          command_number: data.command_number,
          article_name: data.article_name,
          department: data.department,
          comments: data.comments,
          archived_by: data.archived_by ? data.archived_by.id : null,
          directed_by: data.directed_by ? data.directed_by.id : null,
          cost_detail: data.cost_detail,
          archived_at: data.archived_at,
        });

        this.isLoaded = true

      }).catch((error) => {
        console.error("Erreur lors de la récupération de l'anomalie :", error);
        this.$notify({
          message: 'Impossible de récupérer l\'anomalie spécifiée',
          type: 'danger',
          verticalAlign: 'bottom'
        });
        this.back(); // Redirection en cas d'erreur
      });
    } else {
      this.axios.get(process.env.VUE_APP_API_BASE_URL + `anomaly/last`).then((response) => {
        let currentYear = new Date().getFullYear()
        currentYear = currentYear.toString().slice(-2)

        if(response.data.last === null){
          this.anomaly.num = 'ANO-'+currentYear+'-01'
        } else {
          let lastCommandYear = response.data.last.num.slice(-5, -3)
          if(lastCommandYear === currentYear){
            let anomalyNumber = response.data.last.num.slice(-2)
            anomalyNumber = parseInt(anomalyNumber)
            anomalyNumber++
            if(anomalyNumber <= 9){
              anomalyNumber = '0'+anomalyNumber
            } else {
              anomalyNumber = anomalyNumber.toString()
            }
            this.anomaly.num = 'ANO-'+currentYear+'-'+anomalyNumber

          } else {
            this.anomaly.num = 'ANO-'+currentYear+'-01'
          }
        }
      })
      this.isLoaded = true
    }
  },
  methods: {
    back(){
      this.$router.push({ name: 'Anomalies List' })
    },
    checkDuplicateNum(){

    },
    getRemoteCommand(query){
      if (query.length > 5 && (this.command.loaded || this.command.options.length === 0)) {
        this.command.options = [];
        this.command.loaded = false;

        this.axios.get(process.env.VUE_APP_API_BASE_URL +'command/search', {params: {q: query}})
            .then(response => {
              this.command.options = response.data;
              this.command.loaded = true;
            })
            .catch(error => {
              console.error(error);
            });
      } else {
        this.command.options = [];
      }
    },
    getRemoteArticle(query){
      if (this.article.loaded || this.article.options.length === 0) {
        this.article.options = [];
        this.article.loaded = false;

        this.axios.get(process.env.VUE_APP_API_BASE_URL +'article/search', {params: {q: query}})
            .then(response => {
              this.article.options = response.data;
              this.article.loaded = true;
            })
            .catch(error => {
              console.error(error);
            });
      }
    },
    getRemoteCustomer(query){
      if (query.length > 2 && (this.customer.loaded || this.customer.options.length === 0)) {
        this.customer.options = [];
        this.customer.loaded = false;

        this.axios.get(process.env.VUE_APP_API_BASE_URL +'customer/search', {params: {q: query}})
            .then(response => {
              this.customer.options = response.data;
              console.log(this.customer.options)
              this.customer.loaded = true;
            })
            .catch(error => {
              console.error(error);
            });
      }
    },
    save() {
      this.$validator.validateAll().then((isValid) => {
        if (isValid) {
          this.busy = true;

          if (this.anomaly.id) {
            // Mode édition : utiliser `put` pour mettre à jour
            this.anomaly.put(process.env.VUE_APP_API_BASE_URL + `anomaly/update/` + this.anomaly.id)
                .then(() => {
                  this.busy = false;
                  this.$notify({
                    message: 'L\'anomalie a été mise à jour avec succès.',
                    type: 'success',
                    verticalAlign: 'bottom'
                  });
                  this.back();
                })
                .catch((error) => {
                  console.error("Erreur lors de la mise à jour de l'anomalie :", error);
                  this.busy = false;
                });
          } else {
            // Mode création : utiliser `post` pour ajouter
            this.anomaly.post(process.env.VUE_APP_API_BASE_URL + `anomaly/add`)
                .then(() => {
                  this.busy = false;
                  this.$notify({
                    message: 'L\'anomalie a été créée avec succès.',
                    type: 'success',
                    verticalAlign: 'bottom'
                  });
                  this.back();
                })
                .catch((error) => {
                  console.error("Erreur lors de la création de l'anomalie :", error);
                  this.busy = false;
                });
          }
        } else {
          this.busy = false;
        }
      });
    }

  }
}
</script>

<style>

.has-danger .el-input input{
  color: #000;
  background-color: #ffffff;
  border-radius: 10px;
  border: 1px solid #ffcfcf !important;
}

.has-danger textarea {
  border: 1px solid #ffcfcf !important;
  border-radius: 12px;
}


</style>